if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          swiperContainer: this.querySelector('[id^="ProductSwiper"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.elements.viewer && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        try {
          // Support for Swiper layout
          if (this.elements.swiperContainer) {
            const activeSlide = this.elements.swiperContainer.querySelector(`[data-media-id="${mediaId}"]`);
            if (activeSlide) {
              // Find Swiper instance - try multiple ways to access it
              const swiperInstance = this.elements.swiperContainer.swiper || 
                                     window.Swiper?.getInstance?.(this.elements.swiperContainer) ||
                                     (this.elements.swiperContainer.classList.contains('swiper-initialized') ? 
                                      this.elements.swiperContainer.swiper : null);
              
              if (swiperInstance && typeof swiperInstance.slideTo === 'function') {
                try {
                  const slideIndex = Array.from(activeSlide.parentElement.children).indexOf(activeSlide);
                  if (slideIndex !== -1) {
                    swiperInstance.slideTo(slideIndex);
                  }
                } catch (error) {
                  console.warn('Failed to slide Swiper:', error);
                }
              }
              // Update active state
              this.elements.swiperContainer.querySelectorAll('[data-media-id]').forEach((element) => {
                element.classList.remove('is-active');
              });
              activeSlide.classList.add('is-active');
            }
            // Still update thumbnails if they exist
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (activeThumbnail) {
                this.setActiveThumbnail(activeThumbnail);
              }
            }
            return;
          }
        } catch (error) {
          console.warn('Error in setActiveMedia (Swiper):', error);
          // Continue to standard layout fallback
        }

        try {
          // Standard layout support
          if (!this.elements.viewer) {
            return;
          }
          const activeMedia =
            this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
            this.elements.viewer.querySelector('[data-media-id]');
          if (!activeMedia) {
            return;
          }
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
          });
          activeMedia?.classList?.add('is-active');

          if (prepend) {
            try {
              if (activeMedia.parentElement && activeMedia.parentElement.firstChild !== activeMedia) {
                activeMedia.parentElement.prepend(activeMedia);
              }
            } catch (error) {
              console.warn('Failed to prepend active media:', error);
            }

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (activeThumbnail && activeThumbnail.parentElement) {
                try {
                  if (activeThumbnail.parentElement.firstChild !== activeThumbnail) {
                    activeThumbnail.parentElement.prepend(activeThumbnail);
                  }
                } catch (error) {
                  console.warn('Failed to prepend thumbnail:', error);
                }
              }
            }

            if (this.elements.viewer && this.elements.viewer.slider) {
              try {
                this.elements.viewer.resetPages();
              } catch (error) {
                console.warn('Failed to reset pages:', error);
              }
            }
          }

          this.preventStickyHeader();
          window.setTimeout(() => {
            try {
              if (!this.mql.matches || this.elements.thumbnails) {
                if (activeMedia.parentElement) {
                  activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
                }
              }
              const activeMediaRect = activeMedia.getBoundingClientRect();
              // Don't scroll if the image is already in view
              if (activeMediaRect.top > -0.5) return;
              const top = activeMediaRect.top + window.scrollY;
              window.scrollTo({ top: top, behavior: 'smooth' });
            } catch (error) {
              console.warn('Error scrolling to active media:', error);
            }
          }, 0);
          this.playActiveMedia(activeMedia);

          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            if (activeThumbnail.dataset.mediaPosition) {
              this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            }
          }
        } catch (error) {
          console.warn('Error in setActiveMedia (standard layout):', error);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
